/**
 * Share.web.js — web/Electron polyfill for React Native's `Share.share`.
 *
 * react-native-web's Share calls `window.navigator.share(...)` if it exists
 * and otherwise rejects with "Share is not supported in this browser". The
 * Web Share API is only exposed in:
 *   - Mobile browsers (always)
 *   - Desktop Chrome / Edge on HTTPS pages
 *   - Some PWAs
 *
 * Electron (file:// or app:// origin) does NOT expose navigator.share, so
 * every `Share.share(...)` call across the app silently rejects — the user
 * taps a Share button and nothing happens. ~10 call sites are affected:
 * notes, health passport, vault credentials, snapshot URLs, document
 * download links, AI response export, etc.
 *
 * Behavior:
 *   1. If navigator.share is available, defer to it (matches mobile UX —
 *      iOS Safari surfaces the OS share sheet, Chrome on Android opens
 *      the system picker).
 *   2. Otherwise, copy the most useful field (`url` first, then `message`)
 *      to the clipboard, render a small DOM toast confirming the copy, and
 *      resolve with the same shape RN's Share resolves with on iOS
 *      (`{ action: 'sharedAction' }`). Call sites that wrap in try/catch
 *      treat this as success — no spurious "Share failed" notifications.
 *
 * The toast lives independently of any in-app notification system so the
 * polyfill works from module-init time and inside Modal stacks where the
 * RN-Web notification provider may not yet be mounted (or may render
 * behind another portal).
 */

const TOAST_STYLE_ID = '__eleplan_share_toast_styles';
const TOAST_ROOT_ID = '__eleplan_share_toast_root';

const TOAST_STYLE = `
  #${TOAST_ROOT_ID} {
    position: fixed;
    left: 50%;
    bottom: 32px;
    transform: translateX(-50%) translateY(8px);
    z-index: 2147483601;
    background: rgba(20, 22, 26, 0.94);
    color: #ffffff;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
    padding: 11px 18px;
    border-radius: 999px;
    box-shadow: 0 8px 24px rgba(0,0,0,0.28);
    opacity: 0;
    pointer-events: none;
    transition: opacity 160ms ease-out, transform 160ms ease-out;
    max-width: 80vw;
    text-align: center;
  }
  #${TOAST_ROOT_ID}.__visible {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
`;

function injectToastStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(TOAST_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = TOAST_STYLE_ID;
  style.textContent = TOAST_STYLE;
  document.head.appendChild(style);
}

function showToast(message) {
  if (typeof document === 'undefined' || !document.body) return;
  injectToastStyles();
  // Replace any existing toast so rapid Share calls don't pile up.
  const existing = document.getElementById(TOAST_ROOT_ID);
  if (existing && existing.parentNode) existing.parentNode.removeChild(existing);

  const el = document.createElement('div');
  el.id = TOAST_ROOT_ID;
  el.textContent = message;
  document.body.appendChild(el);

  // Force the browser to commit the initial styles before adding the
  // visible class — otherwise the transition won't fire.
  // eslint-disable-next-line no-unused-expressions
  el.getBoundingClientRect();
  el.classList.add('__visible');

  setTimeout(() => {
    el.classList.remove('__visible');
    setTimeout(() => {
      if (el.parentNode) el.parentNode.removeChild(el);
    }, 220);
  }, 1800);
}

async function copyToClipboard(text) {
  if (typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // Fall through to execCommand fallback below — Clipboard API may
      // be blocked when the document isn't focused (rare in Electron, but
      // can happen if Share is triggered from a background timer).
    }
  }
  // Legacy fallback: hidden textarea + execCommand('copy'). Required for
  // Electron windows that use file:// without explicit clipboard permission.
  if (typeof document === 'undefined') return false;
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '-1000px';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand && document.execCommand('copy');
    document.body.removeChild(ta);
    return !!ok;
  } catch {
    return false;
  }
}

class Share {
  static async share(content, _options) {
    const safeContent = (content && typeof content === 'object') ? content : {};
    const url = typeof safeContent.url === 'string' ? safeContent.url : '';
    const message = typeof safeContent.message === 'string' ? safeContent.message : '';
    const title = typeof safeContent.title === 'string' ? safeContent.title : '';

    // Prefer the OS share sheet when the runtime exposes it (mobile web,
    // PWAs, Chrome on HTTPS). Falls back to clipboard on rejection so the
    // user still gets a useful result if the OS sheet errors out.
    if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
      try {
        await navigator.share({ url: url || undefined, text: message || undefined, title: title || undefined });
        return { action: 'sharedAction' };
      } catch (err) {
        // AbortError = user dismissed the share sheet. Don't fall back to
        // clipboard in that case — that would surprise them with a copy
        // they didn't ask for. Re-resolve as "dismissed" to mirror iOS RN.
        if (err && (err.name === 'AbortError' || /abort/i.test(String(err.message || '')))) {
          return { action: 'dismissedAction' };
        }
        // Any other failure (NotAllowed, security errors, etc.) → fall
        // through to the clipboard path.
      }
    }

    // Clipboard fallback — pick the most useful payload.
    const payload = url || message || title;
    if (!payload) {
      return { action: 'dismissedAction' };
    }
    const copied = await copyToClipboard(payload);
    showToast(copied ? (url ? 'Link copied to clipboard' : 'Copied to clipboard') : 'Share isn\u2019t supported here');
    return { action: copied ? 'sharedAction' : 'dismissedAction' };
  }

  static get sharedAction() { return 'sharedAction'; }
  static get dismissedAction() { return 'dismissedAction'; }
}

export default Share;
export { Share };
